/**
 * FeedList — list adapter for GET /api/v1/feed/.
 *
 * ACT-INDEEP-PRODUCTION-FEED-FIGMA-REPAIR01 (Phase D)
 *
 * Native semantic <ul> of FeedPostCard rows, sourced from
 * `listFeedPostsAdapter`. Loading / empty / error states are
 * explicit and stable. No raw exception leak: error copy is
 * closed-set.
 *
 * Network authority lives in `api/feedPosts.ts`; this component is
 * responsible only for async UI state + projection.
 *
 * Anti-Paper-RED rules bound here:
 *
 *   R1: PostResponse.author carries NO `age` field. The wire shape
 *       is the strongest R1 witness — see FeedList.test.tsx.
 *   R2: the fetch call goes through `listFeedPostsAdapter`, which
 *       uses the Fetch API's GET default. Tests assert the
 *       EFFECTIVE method via `init?.method ?? 'GET'`.
 *   R3: anonymous projection is driven by `is_anonymous` boolean.
 *       The hostile displayName passes through to FeedPostCard,
 *       which projects "Аноним" when `isAnonymous === true`.
 */
import { useEffect, useState } from 'react';
import {
  listFeedPostsAdapter,
  type FeedPost,
  type ListFeedPostsResult,
} from '@/api/feedPosts';
import { deriveInitials } from '@/features/comments/model';
import { FeedPostCard } from './FeedPostCard';
import './FeedList.css';

/**
 * The async UI states for the feed list.
 *
 * Discriminated union so consumers (tests, future ErrorBoundary,
 * etc.) can pattern-match precisely. The `error` state is closed-set
 * — never carries raw exception text — so the rendered copy is
 * always authoritative.
 */
export type FeedListState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly posts: readonly FeedPost[] }
  | { readonly kind: 'empty' }
  | { readonly kind: 'error' };

export interface FeedListProps {
  /**
   * Reference time for the relative-time projection on each card.
   * Tests pass this explicitly so the assertion is deterministic.
   * Defaults to `new Date()` (current time).
   */
  readonly referenceTime?: Date;
  /**
   * Port override for tests. Defaults to `listFeedPostsAdapter`.
   * Phase-D production code path uses the default adapter.
   */
  readonly port?: typeof listFeedPostsAdapter;
}

/**
 * Feed list — Figma 38:904 "Без фильтров" Feed body.
 *
 * On mount: calls `GET /api/v1/feed/` exactly once via the
 * injected port. Renders a native semantic <ul> of FeedPostCard
 * rows. Loading / empty / error states are explicit.
 */
export function FeedList({
  referenceTime,
  port = listFeedPostsAdapter,
}: FeedListProps): JSX.Element {
  const [state, setState] = useState<FeedListState>({ kind: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    (async () => {
      const result: ListFeedPostsResult = await port.list({
        signal: controller.signal,
      });
      if (cancelled) return;
      if (result.kind === 'ok') {
        if (result.page.posts.length === 0) {
          setState({ kind: 'empty' });
        } else {
          setState({ kind: 'ready', posts: result.page.posts });
        }
        return;
      }
      setState({ kind: 'error' });
    })().catch(() => {
      if (!cancelled) setState({ kind: 'error' });
    });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [port]);

  const refTime = referenceTime ?? new Date();

  // The native semantic <ul data-testid="feed-list"> is ALWAYS
  // rendered, regardless of state. Loading / empty / error messages
  // are nested INSIDE the <ul> as <li> children so the element
  // exists in the DOM even when there are no posts to display.
  //
  // Rationale: the post-implementation contract (FeedList.test.tsx
  // P1-04) asserts the <ul> element exists when posts.length === 0,
  // and Playwright F-5/F-6 query descendants of `/feed` (which
  // includes the <ul>). Removing the <ul> in non-ready states would
  // either fail those tests or require a brittle DOM tree to work
  // around.
  return (
    <ul
      className={`feed-list feed-list--${state.kind}`}
      data-testid="feed-list"
      aria-busy={state.kind === 'loading' ? 'true' : 'false'}
    >
      {state.kind === 'loading' && (
        <li
          className="feed-list-loading-row"
          data-testid="feed-list-loading"
          aria-busy="true"
        >
          <div className="feed-list-skeleton-row" />
          <div className="feed-list-skeleton-row" />
          <div className="feed-list-skeleton-row" />
        </li>
      )}

      {state.kind === 'error' && (
        <li
          className="feed-list-error-row"
          data-testid="feed-list-error"
          role="alert"
        >
          Не удалось загрузить ленту. Попробуйте позже.
        </li>
      )}

      {state.kind === 'empty' && (
        <li
          className="feed-list-empty-row"
          data-testid="feed-list-empty"
        >
          В ленте пока нет записей.
        </li>
      )}

      {state.kind === 'ready' && state.posts.map((post) => {
        const isAnon = post.author.is_anonymous === true;
        const avatarInitials = isAnon
          ? ANONYMOUS_INITIAL
          : deriveInitials(post.author.display_name);
        return (
          <li
            key={post.id}
            className="feed-list-item"
            data-testid={`feed-list-item-${post.id}`}
          >
            <FeedPostCard
              postId={post.id}
              authorName={post.author.display_name}
              isAnonymous={isAnon}
              authorInitials={avatarInitials}
              createdAt={post.created_at}
              referenceTime={refTime}
              likeCount={post.like_summary.count}
              body={post.content}
              commentCount={post.comment_count ?? 0}
            />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Initial used for anonymous posts' avatar.
 *
 * Figma 38:904 shows "А" as the initial for the "Аноним" avatar.
 * Pinned as a constant so a future rename cannot silently diverge.
 */
const ANONYMOUS_INITIAL = 'А';